import { DocumentNode } from '@apollo/client';
import { useTranslation } from 'next-i18next/pages';
import { HTMLAttributeAnchorTarget } from 'react';

import { InviteCardCtaOrder, useMultipleVariantData } from '@pflegia/feature-flags';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';

import LearnMoreButton from '@components/position-card/LearnMoreButton.component';

import OffersDialogueButton from '../dialogue';

export interface InviteCtaButtonsProps {
  employerName: string;
  handleAfterAccept?: () => void;
  isLinkLoadingDisabled?: boolean;
  jobId: string;
  jobTitle: string;
  onLearnMoreClick?: () => void;
  openDialog: (connectionInfo: ConnectionInfo) => void;
  positionLink: string;
  refetchQueries?: DocumentNode[];
  slug?: string | null;
  target?: HTMLAttributeAnchorTarget;
}

export const InviteCtaButtons: React.FC<InviteCtaButtonsProps> = ({
  employerName,
  handleAfterAccept,
  isLinkLoadingDisabled = false,
  jobId,
  jobTitle,
  onLearnMoreClick,
  openDialog,
  positionLink,
  refetchQueries,
  slug,
  target,
}) => {
  const { t } = useTranslation('job', { keyPrefix: 'positionCard.buttons' });
  const { isTest } = useMultipleVariantData(InviteCardCtaOrder.Name);

  const learnMore = (
    <LearnMoreButton
      className='px-4! whitespace-nowrap'
      data-testid='learn-more-position-link-invitation-pending'
      href={positionLink}
      isLinkLoadingDisabled={isLinkLoadingDisabled}
      key='learn-more'
      onClick={onLearnMoreClick}
      target={target}
      title={t('learnMore')}
    />
  );

  const decline = (
    <OffersDialogueButton
      employerName={employerName}
      jobPositionId={jobId}
      jobTitle={jobTitle}
      key='decline'
      label={t('decline')}
      openDialog={openDialog}
      refetchQueries={refetchQueries}
      slug={slug}
      status='declined'
    />
  );

  const accept = (
    <OffersDialogueButton
      className='col-span-2 sm:col-span-1'
      employerName={employerName}
      handleAfterAccept={handleAfterAccept}
      jobPositionId={jobId}
      jobTitle={jobTitle}
      key='accept'
      label={t('accept')}
      openDialog={openDialog}
      refetchQueries={refetchQueries}
      slug={slug}
      status='pending'
      type='invitation'
    />
  );

  return (
    <div className='grid w-full grid-cols-2 grid-rows-2 gap-2 sm:w-auto sm:grid-cols-3 sm:grid-rows-1'>
      {isTest ? [accept, decline, learnMore] : [learnMore, decline, accept]}
    </div>
  );
};

export default InviteCtaButtons;
